import {useQuery} from '@tanstack/react-query';
import {AxiosError, AxiosResponse} from 'axios';
import {AxiosErrorToast} from '../general/ReusableFunctions';
import {IApiDataResponse, IApiPaginationResponse} from '../../dto/API.dto';
import axiosInstance, {apiGet} from '../../axiosConfig';
import {ICompany} from '../../dto/Company.dto';
import {ITaskItem} from '../../dto/Task.dto';
import {IMyTaskFilterProp} from './ListTasks';
import {IRole} from '../../dto/Role.dto';
import {taskFilterSearchProp} from '../../pages/staff-landing-page/StaffLandingTaskList';

export type ICompanyTasksResp = ICompany & {
  tasks: ICustomTaskUserList[];
};
interface ICustomTaskUserList extends ITaskItem {
  assignees: {
    id: number;
    name: string;
  }[];
}

export const getTasksListOfRoleApiPath = (
  roleId: number,
  userId?: number | null,
) => {
  return `v1/task/role/${roleId}/user` + (userId ? '/' + userId : '');
};

const getTaskUsersList = (user_id: number | null = null, role_id: number) => {
  return axiosInstance
    .get(getTasksListOfRoleApiPath(role_id, user_id))
    .then((response: AxiosResponse<IApiDataResponse<ICompanyTasksResp[]>>) => {
      return response.data.data;
    })
    .catch((error: AxiosError) => {
      AxiosErrorToast(error, 'Error in listing task');
      throw error;
    });
};

export const useRqTasksByRole = (
  user_id: number | null,
  role_id: number,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [getTasksListOfRoleApiPath(role_id, user_id)],
    queryFn: () => getTaskUsersList(user_id, role_id),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled,
  });
};
export const getRoleTaskList = (searchVal: string) => {
  const baseUrl = `v1/task/role/list`;
  const searchParam = searchVal
    ? `?search=${encodeURIComponent(searchVal)}`
    : '';
  return baseUrl + searchParam;
};
const getRoleTask = (searchVal: taskFilterSearchProp | undefined) => {
  return axiosInstance
    .get('v1/task/role/list', {params: searchVal})
    .then((response: AxiosResponse<IApiPaginationResponse<ITaskItem[]>>) => {
      return response.data;
    })
    .catch((error: AxiosError) => {
      AxiosErrorToast(error, 'Error in listing task');
      throw error;
    });
};
export const useRqTaskByRoleId = (
  searchVal: taskFilterSearchProp | undefined,
  tabValue: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['v1/task/role/list', searchVal, tabValue],
    queryFn: () => getRoleTask(searchVal),
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};

// Division / Department / Subgroup - Task filter.
const getRoleFilterList = (params: RoleTypeFilterProp) => {
  return apiGet(`/v1/task/role/filter`, {params: params}).then(
    (response: AxiosResponse<IApiDataResponse<IRole[]>>) => {
      return response.data.data;
    },
  );
};
type RoleTypeFilterProp = {
  role_type: string;
  division_slug?: string;
  department_slug?: string;
};
export const useRqMyRoleFilterList = (
  query: RoleTypeFilterProp,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['v1/task/role/filter', query],
    queryFn: () => getRoleFilterList(query),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};

// Task filter status filter
const getTaskStatusFilterList = (params: TaskStatusFilterProp) => {
  return apiGet(`/v1/task/role/status-filter`, {params: params}).then(
    (response: AxiosResponse<IApiDataResponse<IMyTaskFilterProp[]>>) => {
      return response.data.data;
    },
  );
};
export type TaskStatusFilterProp = {
  division_slug?: string;
  department_slug?: string;
  subgroup_slug?: string;
};
export const useRqMyTaskStatusFilterList = (
  query: TaskStatusFilterProp,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['v1/task/role/status-filter', query],
    queryFn: () => getTaskStatusFilterList(query),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled,
  });
};
